import { Button, Checkbox, Tooltip, type CheckboxChangeEvent } from 'antd';
import { EditIcon } from '@protector/icons';
import { GroupCollapse, type GroupCollapseItem } from '@protector/components';
import { type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import type { NetworkTrapGroup, NetworkTrapRow } from '../network-management.models';
import {
  NETWORK_CARD_LIST_HEADER_EDIT_BUTTON,
  NETWORK_CARD_LIST_SELECT_ALL
} from '@features/regional-overview/digital-traps/domain/tests-id.constants';
import { isSelectionDisabled } from '../network-management.utils';
import { useNetworkManagementStore } from '../network-management.zustand';
import NetworkTrapCard from '../network-trap-card';
import styles from './network-card-list.module.css';

export interface NetworkCardListProps {
  groups: NetworkTrapGroup[];
  dataTestId?: string;
}

export const NetworkCardList = ({ groups, dataTestId }: NetworkCardListProps) => {
  const [t] = useTranslation();
  const { selectedTrapIDs, selectedTraps, selectedPestEppoCode, setSelectedTraps } = useNetworkManagementStore();

  const handleCheckedChange = (record: NetworkTrapRow, checked: boolean) => {
    setSelectedTraps(checked ? [...selectedTraps, record] : selectedTraps.filter(row => row.id !== record.id));
  };

  const handleEditTitle = (clickEvent: MouseEvent<HTMLElement>, onEditTitle?: () => void) => {
    clickEvent.stopPropagation();
    onEditTitle?.();
  };

  const handleSelectAllChange = (group: NetworkTrapGroup, checked: boolean) => {
    const groupRowIDs = new Set(group.tableDataSource.map(row => row.id));

    if (checked) {
      const selectableRows = group.tableDataSource.filter(row => !isSelectionDisabled(row, selectedPestEppoCode));
      const newlySelectedRows = selectableRows.filter(row => !selectedTrapIDs.includes(row.id));
      setSelectedTraps([...selectedTraps, ...newlySelectedRows]);
    } else {
      setSelectedTraps(selectedTraps.filter(row => !groupRowIDs.has(row.id)));
    }
  };

  const collapseItems: GroupCollapseItem[] = groups.map(group => {
    const selectableRows = group.tableDataSource.filter(row => !isSelectionDisabled(row, selectedPestEppoCode));
    const selectedInGroupCount = group.tableDataSource.filter(row => selectedTrapIDs.includes(row.id)).length;
    const allSelectableSelected = selectableRows.length > 0 && selectableRows.every(row => selectedTrapIDs.includes(row.id));
    const isSelectAllDisabled = selectableRows.length === 0;
    const isBlockedByPestGuard = isSelectAllDisabled && group.tableDataSource.length > 0;

    return {
      key: group.key,
      arrow: 'inline',
      title: group.title,
      leading: (
        <Tooltip
          title={isBlockedByPestGuard ? t('pages.regional_overview.digital_traps.network_management.selection_disabled_tooltip') : null}>
          <Checkbox
            data-testid={NETWORK_CARD_LIST_SELECT_ALL}
            checked={allSelectableSelected}
            indeterminate={selectedInGroupCount > 0 && !allSelectableSelected}
            disabled={isSelectAllDisabled}
            onClick={clickEvent => clickEvent.stopPropagation()}
            onChange={(event: CheckboxChangeEvent) => handleSelectAllChange(group, event.target.checked)}
          />
        </Tooltip>
      ),
      trailing: group.onEditTitle ? (
        <Button
          className={styles.groupHeaderEditButton}
          data-testid={NETWORK_CARD_LIST_HEADER_EDIT_BUTTON}
          type='text'
          size='small'
          tabIndex={0}
          aria-label={t('general.edit')}
          title={t('general.edit')}
          onClick={clickEvent => handleEditTitle(clickEvent, group.onEditTitle)}
          icon={<EditIcon className={styles.editIcon} />}
        />
      ) : undefined,
      children: (
        <ul className={styles.groupCardList}>
          {group.tableDataSource.map(record => (
            <li className={styles.cardListItem} key={record.key}>
              <NetworkTrapCard
                record={record}
                checked={selectedTrapIDs.includes(record.id)}
                onCheckedChange={handleCheckedChange}
                selectedPestEppoCode={selectedPestEppoCode}
              />
            </li>
          ))}
        </ul>
      )
    };
  });

  return (
    <section
      className={classNames(styles.cardListSection, {
        [styles.cardListSectionWithSelection]: selectedTrapIDs.length > 0
      })}>
      <GroupCollapse dataTestId={dataTestId} items={collapseItems} />
    </section>
  );
};
